import { useEffect, useRef, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import defaultCostParameters from '../costParameters.json';

// Load from localStorage as fallback
const loadFromLocalStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const useCostDataSync = (
  costBaseParams,
  setCostBaseParams,
  costParams,
  setCostParams
) => {
  const subscriptionRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Debounced write function - writes both cost data types in a single update
  const debouncedWriteAllCostData = useRef(
    debounce(async (baseParams, params) => {
      if (!isSupabaseConfigured()) return;
      
      try {
        // Try to update existing row, or insert if it doesn't exist
        const { data: existing } = await supabase
          .from('network_visualizer_data_sync')
          .select('id')
          .limit(1)
          .single();

        const payload = {
          cost_base_params: baseParams,
          cost_params: params,
          updated_at: new Date().toISOString()
        };

        if (existing) {
          await supabase
            .from('network_visualizer_data_sync')
            .update(payload)
            .eq('id', existing.id);
        } else {
          await supabase
            .from('network_visualizer_data_sync')
            .insert(payload);
        }
        
        // Also save to localStorage as backup
        localStorage.setItem('costBaseParams', JSON.stringify(baseParams));
        localStorage.setItem('costParams', JSON.stringify(params));
      } catch (error) {
        console.error('Error syncing cost data to Supabase:', error);
        // Fallback to localStorage only
        localStorage.setItem('costBaseParams', JSON.stringify(baseParams));
        localStorage.setItem('costParams', JSON.stringify(params));
      }
    }, 500)
  ).current;

  // Initial data fetch
  const initializeData = useCallback(async () => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    if (!isSupabaseConfigured()) {
      console.log('Supabase not configured, using localStorage only');
      return;
    }

    try {
      // Fetch all cost data from single table
      const { data, error } = await supabase
        .from('network_visualizer_data_sync')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "no rows returned", which is okay
        console.error('Error fetching cost data:', error);
      } else if (data) {
        // Update both state values from the single row
        if (data.cost_base_params) {
          setCostBaseParams(data.cost_base_params);
        }
        if (data.cost_params) {
          setCostParams(data.cost_params);
        }
      }
    } catch (error) {
      console.error('Error initializing data from Supabase:', error);
    }
  }, [setCostBaseParams, setCostParams]);

  // Set up real-time subscription
  useEffect(() => {
    initializeData();

    if (!isSupabaseConfigured()) return;

    // Subscribe to network_visualizer_data_sync changes
    const channel = supabase
      .channel('network_visualizer_data_sync_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'network_visualizer_data_sync'
        },
        (payload) => {
          console.log('Cost data changed:', payload);
          if (payload.new) {
            // Update both state values when data changes
            if (payload.new.cost_base_params) {
              setCostBaseParams(payload.new.cost_base_params);
            }
            if (payload.new.cost_params) {
              setCostParams(payload.new.cost_params);
            }
          }
        }
      )
      .subscribe();

    // Cleanup on unmount
    return () => {
      channel.unsubscribe();
    };
  }, [initializeData, setCostBaseParams, setCostParams]);

  // Watch for local changes and sync to Supabase
  useEffect(() => {
    if (!isInitializedRef.current) return;
    debouncedWriteAllCostData(costBaseParams, costParams);
  }, [costBaseParams, costParams, debouncedWriteAllCostData]);

  return {
    isSupabaseEnabled: isSupabaseConfigured()
  };
};
